<template>
  <div class="evaluation-grid">
    <div class="evaluation-grid__header">
      <BaseButton
        v-if="!pending && !notFound"
        has-icon-left
        isGrey
        isSmall
        isSquare
        :tooltip-text="$t('actions.edit-evaluation-grid')"
        @click.native="openEditEvalGrid"
      >
        <BaseIcon name="edit-2" />
        {{ $t('actions.edit') }}
      </BaseButton>
      <BaseButton
        v-if="tenantHasLLMExtractionsAccess() && !pending && !notFound"
        has-icon-left
        isGrey
        isSmall
        isSquare
        :disabled="isEvalGridFieldsExtracting"
        :tooltip-text="
          isEvalGridFieldsExtracting
            ? $t('actions.extraction-in-progress')
            : $t('page.opportunity-details.regenerate-tooltip')
        "
        @click.native="onLlmExtractionGenerateClick"
      >
        <BaseIcon name="rotate-cw" />
        {{ $t('actions.regenerate') }}
      </BaseButton>
    </div>
    <FieldsExtractionProgress />
    <div class="evaluation-grid__content">
      <BaseCircularLoadingBar v-if="pending" :size="32" />
      <div v-else-if="notFound" class="evaluation-grid__empty-state">
        {{ $t('evaluation-grid.not-found') }}
      </div>
      <div v-else class="evaluation-grid__grid-content">
        <DocumentsSummaryItem
          v-for="(field, index) in evaluationFields"
          :key="index"
          :item="field"
          isCompact
          isOnAnalysisPage
          @on-add="addValue"
          @on-delete="deleteValue"
        />
        <BaseAppendButton @execute="openNewFieldDialog" has-icon-left class="evaluation-grid__add-field">
          <BaseIcon name="plus" />
          {{ $t('evaluation-grid.add-field.add-new-field') }}
        </BaseAppendButton>
        <span class="evaluation-grid__divider"></span>
        <MarkdownEditor
          :title="$t('evaluation-grid.notes.title')"
          :value="(opportunity && opportunity.notes) || ''"
          :saving="notesSaving"
          :editor-options="editorOptions"
          :auto-save="true"
          :on-conflict-refresh="refreshOpportunityBeforeEditing"
          :before-edit="refreshOpportunityBeforeEditing"
          @input="(v) => $set(opportunity, 'notes', v)"
          @save="(markdown) => saveOpportunityNotes(markdown)"
        />
      </div>

      <AddEvalGridFieldDialog
        ref="addNewFieldDialog"
        :buttonText="$t('actions.save')"
        :evaluation-grid-fields="evaluationFields"
        show-local-copy-notice
        @submit="createNewField($event)"
      />
      <v-overlay :value="pending" absolute opacity="0.5" color="#ffffff" z-index="1">
        <BaseCircularLoadingBar is-small />
      </v-overlay>
    </div>
    <EditEvalGridDialog ref="editEvalGrid" />
    <LlmExtractionGenerateDialog ref="llmExtractionGenerateDialog" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AddEvalGridFieldDialog from '@/components/page-pdf-analysis/AddEvaluationGridFieldDialog.vue';
import BaseAppendButton from '@/components/base/BaseAppendButton.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import DocumentsSummaryItem from '@/components/page-opportunity-details/DocumentsSummaryItem';
import FieldsExtractionProgress from '@/components/page-opportunity-details/FieldsExtractionProgress.vue';
import EditEvalGridDialog from '@/components/page-pdf-analysis/EditEvalGridDialog';
import LlmExtractionGenerateDialog from '@/components/page-opportunity-details/LlmExtractionGenerateDialog.vue';
import MarkdownEditor from '@/components/form/MarkdownEditor.vue';
import { tenantHasLLMExtractionsAccess } from '@/util/module-access-util';

export default {
  name: 'EvaluationGrid',
  components: {
    BaseAppendButton,
    BaseCircularLoadingBar,
    BaseIcon,
    BaseButton,
    MarkdownEditor,
    DocumentsSummaryItem,
    FieldsExtractionProgress,
    LlmExtractionGenerateDialog,
    AddEvalGridFieldDialog,
    EditEvalGridDialog,
  },
  props: {
    opportunityId: String,
  },
  data() {
    return {
      selectedValue: null,
      notesSaving: false,
    };
  },
  computed: {
    ...mapGetters('evaluationGrid', ['evaluationGrid', 'pending', 'evaluationFields', 'error', 'notFound']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('fieldsExtraction', { isEvalGridFieldsExtracting: 'isExtracting' }),
    editorOptions() {
      return {
        placeholder: this.$t('evaluation-grid.notes.placeholder'),
      };
    },
  },
  methods: {
    tenantHasLLMExtractionsAccess,
    ...mapActions('evaluationGrid', [
      'addEvalGridField',
      'addEvalGridValue',
      'deleteEvalGridValue',
      'getEvaluationGrid',
    ]),
    ...mapActions('evaluationGridTemplates', ['getEvaluationGridTemplates']),
    ...mapActions('opportunity', ['getOpportunity', 'editOpportunity']),
    openEditEvalGrid() {
      this.$refs.editEvalGrid.openDialog();
    },
    openNewFieldDialog() {
      this.$refs.addNewFieldDialog.openDialog();
    },
    createNewField(field) {
      this.addEvalGridField({
        opportunityId: this.opportunityId,
        field: field,
      })
        .then(() => {
          this.getEvaluationGrid(this.opportunityId);
          this.$toast.success(this.$t('toast.success'));
          this.$refs.addNewFieldDialog.closeDialog();
        })
        .catch((error) => this.$toast.error(error));
    },
    onLlmExtractionGenerateClick() {
      this.$refs.llmExtractionGenerateDialog.openDialog(this.opportunityId);
    },
    onEvaluationGridFieldValueClick(value) {
      this.selectedValue = value;
    },
    buildOpportunityBody(overrides) {
      return {
        name: this.opportunity.name,
        evaluationGridTemplateId: null,
        statusId: this.opportunity.status ? this.opportunity.status.id : null,
        startDate: this.opportunity.startDate,
        endDate: this.opportunity.endDate,
        source: this.opportunity.source,
        emitter: this.opportunity.emitter,
        notes: this.opportunity.notes ? this.opportunity.notes : '',
        version: this.opportunity.version || 0,

        ...(overrides || {}),
      };
    },
    async saveOpportunityNotes(markdown) {
      try {
        this.notesSaving = true;

        const body = this.buildOpportunityBody({
          notes: markdown,
        });

        await this.editOpportunity({
          opportunityId: this.opportunity.id,
          opportunity: body,
        });

        this.$set(this.opportunity, 'notes', markdown);
      } finally {
        this.notesSaving = false;
      }
    },
    async refreshOpportunityBeforeEditing() {
      if (!this.opportunity || !this.opportunity.id) return;
      await this.getOpportunity({ id: this.opportunity.id, silent: true });
    },
    addValue({ fieldId, value }) {
      this.addEvalGridValue({
        opportunityId: this.opportunity.id,
        fieldId,
        source: 'NO_SOURCE',
        textAnnotationId: null,
        opportunityDocumentId: null,
        value,
        valueType: 'VALUE',
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => this.$toast.error(error));
    },
    deleteValue({ fieldId, value }) {
      this.deleteEvalGridValue({
        opportunityId: this.opportunity.id,
        fieldId,
        value,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => this.$toast.error(error));
    },
  },
  created() {
    this.getEvaluationGridTemplates();
  },
  watch: {
    error(newValue) {
      if (newValue !== null) {
        this.$toast.error(newValue);
      }
    },
  },
};
</script>

<style lang="scss">
.evaluation-grid {
  display: contents;

  &__header {
    background-color: $color-white;
    display: flex;
    gap: 8px;
    height: 32px;
    padding-right: 8px;
    margin-bottom: 8px;
    justify-content: flex-end;

    .title {
      color: $color-normal;
      font-size: 20px;
      font-weight: 800;
      letter-spacing: normal;
      margin-bottom: 1rem;
    }
  }

  &__extracting {
    .base-icon {
      color: $color-error;
      animation: blink 1.5s ease-in-out infinite;
    }
  }

  &__btn {
    background-color: $color-neutral-pale;
    border-radius: $border-radius-root;
    padding: 4px 8px;
    color: $color-normal;
    transition: all 200ms ease;
    cursor: pointer;
    display: flex;
    gap: 4px;
    align-items: center;
    height: 24px;

    .base-icon {
      height: 14px;
      width: 14px;
    }

    &:hover {
      transition: all 200ms ease;
      background-color: $color-hover;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.4;

      &:hover {
        color: $color-normal;
        opacity: 0.4;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    padding-right: 8px;
    height: 100%;
    padding: 4px;

    &::-webkit-scrollbar-track {
      background-color: $color-neutral-pale;
    }

    &::-webkit-scrollbar {
      height: 0;
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $border-radius-large;
      background-color: $color-grey-pale;
    }
  }

  &__grid-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__empty-state {
    color: $color-normal-pale;
    font-size: $font-size-small;
    padding: 16px 8px;
    text-align: center;
  }

  &__add-field {
    margin-top: 8px;
  }

  &__divider {
    background: $color-neutral-pale;
    display: block;
    height: 1px;
    margin-top: 16px;
    width: 100%;
  }

  .rotate-animation {
    animation: rotation 1.4s infinite linear;
  }

  .markdown-editor-field {
    margin-bottom: 24px;

    &__title {
      color: $color-bluey-gray;
      font-weight: 700;
    }
  }

  &__extraction-progress {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    font-size: $font-size-small;
    color: $color-normal-pale;
  }
}
</style>
